/**
 * Profile Service
 *
 * A high-quality centralized solution for managing user and law firm profiles.
 * Provides type-safe access to profile data with caching, validation, and easy-to-use hooks.
 *
 * @example
 * ```tsx
 * // In a React component
 * import { useUserProfile } from '@/lib/services/profile';
 *
 * function ProfileComponent() {
 *   const { profile, isLoading, updateProfile } = useUserProfile();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h1>Welcome, {profile?.displayName}</h1>
 *       <button onClick={() => updateProfile({ phone: '+61 400 123 456' })}>
 *         Update Phone
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // In a server component or action
 * import { getUserProfileAction } from '@/lib/services/profile';
 *
 * async function MyServerComponent() {
 *   const result = await getUserProfileAction();
 *
 *   if (result.success) {
 *     return <div>Hello, {result.data.firstName}</div>;
 *   }
 *
 *   return <div>Error: {result.error}</div>;
 * }
 * ```
 */

// Export all types
export * from './types';

// Export all server actions (kept due to unstable_cache busting + auth /
// security sensitivity — see ./CLAUDE.md)
export {
  getCurrentUserIdAction,
  getUserProfileAction,
  updateUserProfileAction,
  logoutOtherSessionsAction,
  uploadProfilePhotoAction,
  enable2FAAction,
  disable2FAAction,
  revalidateProfileCacheAction
} from './actions';

// directApiClient-backed mutations (LF-3217)
export { profileApi } from './client';

// Export all utilities
export {
  // Cache management
  getCachedProfile,
  setCachedProfile,
  clearCachedProfile,

  // Profile helpers
  getUserDisplayName,
  getUserInitials
} from './utils';

// Export React hooks
export {
  useUserProfile,
  useLawFirmProfile,
  useCurrentUserId,
  useCurrentUserEmail,
  useProfileQuery,
  useHelpBannerDismissal,
  type HelpBannerKey
} from './hooks';

// `profileKeys` is sourced from the leaf query-keys module (not re-exported
// through the react-refresh-registered `./hooks`) to avoid a Storybook TDZ.
export { profileKeys } from '@/lib/query-keys/profile';
